import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useCallback, useContext, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Tabs, useLocalizeMessage } from 'libs.nucleus';
import { AuthContext, ApiClientService, useApiClient } from 'libs.react_internal';
import { ConsentEditorModal } from '../components/consent_editor_modal.component';
import { getFileNameToSiteIds } from '../consent_agent/consent_agent.types';
import { isSpreadsheetFile, useConsentAgent } from '../consent_agent/use_consent_agent.hook';
import { useConsentContext } from '../hooks/use_consent_context.hook';
import { getAssignments, getEnvironments, getSites } from '../mom_adapter/getters';
import { replaceAllAssignments, setAssignmentsForDocument, setAssignmentsForSite } from '../mom_adapter/setters';
import { SiteConfiguration } from '../site_configuration/site_configuration.component';
import { ConsentManagerUI } from './consent_manager_ui/consent_manager_ui.component';
const getUploadedConsentName = (file) => {
    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    const uploadedAt = new Date().toISOString().replace(/\.\d{3}Z$/, 'Z');
    return isPdf ? `${file.name}-${uploadedAt}` : file.name;
};
export const ConsentView = forwardRef(function ConsentView({ studyId, files, onUpload, onEditRow, onFilesChange }, ref) {
    const translate = useLocalizeMessage();
    const { entityId } = useContext(AuthContext);
    const momClient = useApiClient(ApiClientService.MOM);
    const momClientRef = useRef(momClient);
    momClientRef.current = momClient;
    const [sites, setSites] = useState(undefined);
    const [environments, setEnvironments] = useState(undefined);
    const [assignments, setAssignments] = useState([]);
    const [selectedEnvironmentId, setSelectedEnvironmentId] = useState(undefined);
    const { processFiles } = useConsentAgent(sites);
    const { state, consentFiles, loadDocuments, uploadConsentFile, archiveTemplateVersion, setActiveDocument, activeDocument, fetchDocumentPdf, } = useConsentContext();
    useImperativeHandle(ref, () => ({
        removeFile: (id) => {
            void archiveTemplateVersion(id);
        },
    }), [archiveTemplateVersion]);
    const consentFileList = useMemo(() => consentFiles.map((f) => ({ id: f.id, title: f.title || '' })), [consentFiles]);
    const onFilesChangeRef = useRef(onFilesChange);
    onFilesChangeRef.current = onFilesChange;
    useEffect(() => {
        onFilesChangeRef.current?.(consentFileList);
    }, [consentFileList]);
    useEffect(() => {
        if (!entityId || !studyId) {
            return;
        }
        let cancelled = false;
        async function fetchStudySitesAndEnvironments() {
            const [fetchedSites, fetchedEnvironments] = await Promise.all([
                getSites(momClientRef.current, entityId, studyId),
                getEnvironments(momClientRef.current, entityId, studyId),
            ]);
            if (!cancelled) {
                setSites(fetchedSites);
                setEnvironments(fetchedEnvironments);
            }
        }
        void fetchStudySitesAndEnvironments();
        return () => {
            cancelled = true;
        };
    }, [entityId, studyId]);
    const consentFilesRef = useRef(consentFiles);
    consentFilesRef.current = consentFiles;
    // Fingerprint of (id, templateKey) pairs — these are the only consentFile attributes that
    // affect the assignment expansion. Avoids refetching on unrelated metadata edits.
    const consentFileKeyFingerprint = useMemo(() => consentFiles
        .map((f) => `${f.id}:${f.templateKey}`)
        .sort()
        .join('|'), [consentFiles]);
    useEffect(() => {
        if (!entityId || !studyId || selectedEnvironmentId === undefined) {
            setAssignments((current) => (current.length === 0 ? current : []));
            return;
        }
        let cancelled = false;
        async function loadAssignmentsForEnvironment() {
            const list = await getAssignments(momClientRef.current, entityId, studyId, selectedEnvironmentId, consentFilesRef.current);
            if (!cancelled) {
                setAssignments(list);
            }
        }
        void loadAssignmentsForEnvironment();
        return () => {
            cancelled = true;
        };
    }, [entityId, studyId, selectedEnvironmentId, consentFileKeyFingerprint]);
    useEffect(() => {
        void loadDocuments();
    }, [loadDocuments]);
    useEffect(() => {
        if (files) {
            files.forEach((f) => {
                const existing = consentFiles.find((p) => p.id === f.id);
                if (!existing) {
                    void uploadConsentFile(new File([], f.fileName || f.title || ''), f.fileName || f.title || '', {
                        sites: f.site,
                    });
                }
            });
        }
    }, [files, consentFiles, uploadConsentFile]);
    const [isEditing, setIsEditing] = useState(false);
    const handleEditRow = useCallback(async (id) => {
        const existingDoc = state.documents.find((d) => d.id === id);
        if (existingDoc && !existingDoc.pdfDataUrl) {
            await fetchDocumentPdf(id);
        }
        setActiveDocument(id);
        setIsEditing(true);
        if (onEditRow) {
            onEditRow(new File([], ''), id);
        }
    }, [state.documents, fetchDocumentPdf, setActiveDocument, onEditRow]);
    const handleCloseModal = useCallback(() => {
        setIsEditing(false);
        setActiveDocument(null);
    }, [setActiveDocument]);
    const handleAssignedEnvironmentSitesChange = useCallback(async (documentId, selectedEnvironmentSiteIds, environmentSites) => {
        if (!entityId || !studyId || selectedEnvironmentId === undefined) {
            return;
        }
        const next = await setAssignmentsForDocument(momClientRef.current, entityId, studyId, selectedEnvironmentId, assignments, documentId, selectedEnvironmentSiteIds, environmentSites, consentFilesRef.current);
        setAssignments(next);
    }, [entityId, studyId, selectedEnvironmentId, assignments]);
    const handleAssignedConsentDocumentsChange = useCallback(async (environmentSiteId, selectedConsentDocumentIds, consentDocumentIdsInScope) => {
        if (!entityId || !studyId || selectedEnvironmentId === undefined) {
            return;
        }
        const next = await setAssignmentsForSite(momClientRef.current, entityId, studyId, selectedEnvironmentId, assignments, environmentSiteId, selectedConsentDocumentIds, consentDocumentIdsInScope, consentFilesRef.current);
        setAssignments(next);
    }, [entityId, studyId, selectedEnvironmentId, assignments]);
    const handleReplaceAssignments = useCallback(async (nextAssignments) => {
        if (!entityId || !studyId || selectedEnvironmentId === undefined) {
            return;
        }
        const next = await replaceAllAssignments(momClientRef.current, entityId, studyId, selectedEnvironmentId, nextAssignments, consentFilesRef.current);
        setAssignments(next);
    }, [entityId, studyId, selectedEnvironmentId]);
    const handleFilesUploaded = useCallback(async (uploadedFiles) => {
        if (uploadedFiles.length === 0) {
            return;
        }
        const hasSpreadsheets = uploadedFiles.some(isSpreadsheetFile);
        const response = hasSpreadsheets ? await processFiles(uploadedFiles) : undefined;
        const fileNameToSiteIds = getFileNameToSiteIds(response);
        for (const file of uploadedFiles) {
            if (onUpload) {
                onUpload(file);
            }
            await uploadConsentFile(file, getUploadedConsentName(file), {
                sites: fileNameToSiteIds.get(file.name) ?? '—',
            });
        }
    }, [processFiles, onUpload, uploadConsentFile]);
    const tabItems = useMemo(() => [
        {
            label: translate('Upload files'),
            content: (_jsx(ConsentManagerUI, { files: consentFiles, onFilesUploaded: handleFilesUploaded, onEditRow: handleEditRow, archiveTemplateVersion: archiveTemplateVersion })),
        },
        {
            label: translate('Site configuration'),
            content: (_jsx(SiteConfiguration, { environments: environments, sites: sites, consentFiles: consentFiles, assignments: assignments, selectedEnvironmentId: selectedEnvironmentId, onSelectedEnvironmentIdChange: setSelectedEnvironmentId, onAssignedEnvironmentSitesChange: handleAssignedEnvironmentSitesChange, onAssignedConsentDocumentsChange: handleAssignedConsentDocumentsChange, onReplaceAssignments: handleReplaceAssignments })),
        },
    ], [
        consentFiles,
        sites,
        environments,
        assignments,
        selectedEnvironmentId,
        handleAssignedEnvironmentSitesChange,
        handleAssignedConsentDocumentsChange,
        handleReplaceAssignments,
        handleFilesUploaded,
        handleEditRow,
        archiveTemplateVersion,
    ]);
    return (_jsxs(_Fragment, { children: [_jsx(Tabs, { tabItems: tabItems, defaultIndex: 0 }), _jsx(ConsentEditorModal, { isOpen: isEditing, document: activeDocument, consentFiles: consentFileList, onClose: handleCloseModal, onSelectTemplate: handleEditRow })] }));
});
